import { DiffActionTypeEnum, TransparencyEnum } from "@clockwise/schema";
import { ResponseStatusEnum } from "@clockwise/schema/v2";
import { EventType } from "@clockwise/web-commons/src/util/ActiveEventContext";
import { Interval } from "luxon";
import { ModifiedPlannerEventCardDiff, PlannerEventCard } from "../../web-app-calendar/types";
import { useCurrentProposal } from "../CurrentProposalContext";

const useCurrentProposalCards = (): {
  cardsByDay: PlannerEventCard[];
  modifiedCardDiffs: ModifiedPlannerEventCardDiff[];
  isScheduleByMoving: boolean;
  error: null;
  loading: boolean;
} | null => {
  const { currentProposal } = useCurrentProposal();

  if (!currentProposal.initialized) {
    return null;
  }

  const proposalAttendeeIds = currentProposal.attendees.map((a) => a.person.email);

  // transform it into a PlannerEventCard
  const plannerEventCard: PlannerEventCard = {
    active: false,
    annotaion: undefined,
    deemphasis: false,
    externalEventId: undefined,
    interval: Interval.fromDateTimes(currentProposal.startTime, currentProposal.endTime),
    updatedTimeInterval: undefined,
    currentTimeInterval: undefined,
    proposedResponseState: undefined,
    isAllDay: currentProposal.allDay,
    isCancelled: false,
    smartHold: null, // Only calendar Events need this UI
    diffSummaryId: "", // what does this do
    key: `${currentProposal.title}-${currentProposal.startTime.toISO()}`,
    proposalType: DiffActionTypeEnum.ADD,
    suppressNewBadge: true,
    responseState: "Proposed",
    text: currentProposal.title,
    calendarIds: [],
    attendeesCalendarIds: [], // Intentionally empty so that the event stripes are not shown
    affectedAttendees: [],
    attendees: currentProposal.attendees.map((a) => ({
      __typename: "Attendee",
      id: a.id,
      email: a.person.email,
      responseStatus: a.person.isMe ? ResponseStatusEnum.Accepted : ResponseStatusEnum.NeedsAction,
    })),
    locked: false,
    transparency: TransparencyEnum.None,
    videoLink: null,
    eventPermissions: {
      canInviteOthers: false,
      canModify: false,
      canDelete: false,
      canRemove: false,
      canRSVP: false,
      canSeeOtherGuests: false,
    },
    flexStatus: null,
    attendeeCount: currentProposal.attendees.length,
    proposalMetaData: {
      // Used to determine the width of the proposal event in calendar day view
      // Attendees are set on a proposal only once we display a proposal card and
      // fetch member info from the BE.
      // Here we know the initial attendees from the proposal as well as any others added
      // manually by the user
      attendeeIds:
        proposalAttendeeIds.length > 0 ? proposalAttendeeIds : currentProposal.initialAttendeeIds,
      // Used to determine if a proposal should be rendered in calendar day view
      isConsequence: false,
    },
    type: EventType.Event, // what does this do
  };

  return {
    cardsByDay: [plannerEventCard],
    modifiedCardDiffs: [],
    isScheduleByMoving: false,
    error: null,
    loading: false,
  };
};

export default useCurrentProposalCards;
